import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, X, Sparkles, ChevronDown, ChevronUp, Loader2, Users, RotateCcw, Lightbulb } from 'lucide-react';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { apiFetch } from '@/lib/api';

const QUICK_ANALYSES = [
    { id: 'health-check', label: '🏥 Health Check', prompt: 'health-check' },
    { id: 'growth-plan', label: '📈 Crescimento', prompt: 'growth-plan' },
    { id: 'churn-analysis', label: '🔴 Churn', prompt: 'churn-analysis' },
    { id: 'investor-readiness', label: '💰 Investor Ready', prompt: 'investor-readiness' },
    { id: 'pricing-optimization', label: '💎 Pricing', prompt: 'pricing-optimization' },
    { id: 'cash-forecast', label: '🔮 Caixa', prompt: 'cash-forecast' },
];

function AgentBadge({ agents }) {
    if (!agents || agents.length <= 1) return null;
    return (
        <div className="flex items-center gap-1 mb-1.5">
            <Users className="w-3 h-3 text-foreground-muted" />
            <span className="text-[10px] text-foreground-muted">
                Agentes consultados: {agents.join(', ')}
            </span>
        </div>
    );
}

function MessageBubble({ message }) {
    const isUser = message.role === 'user';

    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
            <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
                {!isUser && <AgentBadge agents={message.agentsConsulted} />}
                <div
                    className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${isUser
                            ? 'bg-cyan-600/20 border border-cyan-600/30 text-foreground'
                            : 'bg-surface-active/60 border border-border-strong/50 text-foreground-secondary'
                        }`}
                >
                    {isUser ? (
                        <p>{message.content}</p>
                    ) : (
                        <div
                            className="prose prose-sm prose-invert max-w-none
                                prose-p:my-1 prose-ul:my-1 prose-ol:my-1
                                prose-li:my-0.5
                                prose-headings:text-foreground prose-headings:mt-3 prose-headings:mb-1
                                prose-strong:text-foreground-strong
                                prose-code:text-cyan-400 prose-code:bg-surface prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
                            dangerouslySetInnerHTML={{ __html: formatMarkdown(message.content) }}
                        />
                    )}
                </div>
                {!isUser && message.usage && (
                    <div className="text-[10px] text-foreground-muted mt-0.5 ml-2">
                        {message.usage.inputTokens + message.usage.outputTokens} tokens
                    </div>
                )}
            </div>
        </div>
    );
}

function formatMarkdown(text) {
    if (!text) return '';
    return text
        // Headers
        .replace(/^### (.+)$/gm, '<h4 class="text-sm font-semibold">$1</h4>')
        .replace(/^## (.+)$/gm, '<h3 class="text-base font-semibold">$1</h3>')
        .replace(/^# (.+)$/gm, '<h2 class="text-lg font-bold">$1</h2>')
        // Bold
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        // Italic
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        // Code
        .replace(/`(.+?)`/g, '<code>$1</code>')
        // Lists
        .replace(/^- (.+)$/gm, '<li>$1</li>')
        .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
        // Group list items
        .replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul class="list-disc pl-4">${match}</ul>`)
        // Line breaks
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br/>');
}

export default function CFOAssistant({ metrics, companies, billingData, trendData }) {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        if (isOpen && !isMinimized) {
            inputRef.current?.focus();
        }
    }, [isOpen, isMinimized]);

    const sendMessage = async (messageText) => {
        if (!messageText?.trim() || isLoading) return;

        const userMessage = { role: 'user', content: messageText.trim() };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);
        setError(null);

        try {
            const res = await apiFetch('/api/internal/ai/analyze', {
                method: 'POST',
                body: JSON.stringify({
                    message: messageText.trim(),
                    metrics,
                    companies: companies?.map(c => ({
                        id: c.id,
                        companyName: c.companyName,
                        cnpj: c.cnpj,
                        planType: c.planType,
                        status: c.status,
                        createdAt: c.createdAt,
                        updatedAt: c.updatedAt,
                        plan: c.plan ? { monthlyPrice: c.plan.monthlyPrice } : null,
                        trialInfo: c.trialInfo,
                    })),
                    billingData,
                    trendData,
                    conversationHistory: messages.slice(-10).map(m => ({
                        role: m.role,
                        content: m.content
                    }))
                })
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Erro ${res.status}`);
            }

            const data = await res.json();

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: data.response,
                agentsConsulted: data.agentsConsulted,
                usage: data.usage,
            }]);
        } catch (err) {
            setError(err.message);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: `❌ Erro: ${err.message}. Verifique se a ANTHROPIC_API_KEY está configurada no .env`,
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickAction = (actionId) => {
        const action = QUICK_ANALYSES.find(a => a.id === actionId);
        if (action) {
            sendMessage(action.prompt === actionId ? getQuickPrompt(actionId) : action.prompt);
        }
    };

    const getQuickPrompt = (id) => {
        const prompts = {
            'health-check': 'Faça um health check completo do negócio. Avalie as métricas atuais vs benchmarks de SaaS B2B e dê uma nota de 0-10 com justificativa.',
            'growth-plan': 'Com base nos dados atuais, qual o plano ideal para dobrar o MRR nos próximos 6 meses? Considere produto, marketing e pricing.',
            'churn-analysis': 'Analise os padrões de churn e retenção. Quais clientes estão em risco? O que fazer para reduzir churn?',
            'investor-readiness': 'Avalie se estamos prontos para uma rodada de investimento. Quais métricas estão boas e quais precisam melhorar antes de um pitch?',
            'pricing-optimization': 'Analise nossa estratégia de pricing atual. O ARPU está adequado? Devemos ajustar planos, criar upgrades ou mudar o modelo?',
            'cash-forecast': 'Projete o fluxo de caixa para os próximos 6 meses considerando MRR atual, churn, novos clientes e inadimplência.',
        };
        return prompts[id] || '';
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(input);
    };

    const clearHistory = () => {
        setMessages([]);
        setError(null);
    };

    // Floating button when closed
    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white px-5 py-3 rounded-2xl shadow-2xl shadow-violet-900/30 transition-all hover:scale-105 active:scale-95"
            >
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold text-sm">CFO AI</span>
            </button>
        );
    }

    return (
        <div className={`fixed bottom-6 right-6 z-50 bg-background border border-border rounded-2xl shadow-2xl shadow-black/50 transition-all duration-300 ${isMinimized ? 'w-80 h-12' : 'w-[420px] h-[600px]'} flex flex-col`}>
            {/* Header */}
            <div
                className="flex items-center justify-between px-4 py-2.5 border-b border-border cursor-pointer select-none"
                onClick={() => isMinimized && setIsMinimized(false)}
            >
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-600 to-cyan-600 flex items-center justify-center">
                        <Sparkles className="w-3.5 h-3.5 text-foreground-strong" />
                    </div>
                    <div>
                        <h3 className="text-xs font-semibold text-foreground">CFO AI Assistant</h3>
                        {!isMinimized && (
                            <p className="text-[10px] text-foreground-muted">Claude Sonnet 4.5 · Multi-agent</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {messages.length > 0 && !isMinimized && (
                        <button
                            onClick={(e) => { e.stopPropagation(); clearHistory(); }}
                            className="p-1 text-foreground-muted hover:text-foreground-muted transition-colors"
                            title="Limpar histórico"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <button
                        onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
                        className="p-1 text-foreground-muted hover:text-foreground-secondary transition-colors"
                    >
                        {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                        className="p-1 text-foreground-muted hover:text-foreground-secondary transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Body */}
            {!isMinimized && (
                <>
                    {/* Messages */}
                    <ScrollArea className="flex-1">
                        <div className="p-4 space-y-1">
                        {messages.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center">
                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-violet-500/20 flex items-center justify-center mb-4">
                                    <Sparkles className="w-7 h-7 text-violet-400" />
                                </div>
                                <h4 className="text-sm font-semibold text-foreground-secondary mb-1">
                                    Olá! Sou seu CFO virtual.
                                </h4>
                                <p className="text-xs text-foreground-muted mb-5 max-w-[280px]">
                                    Analiso suas métricas SaaS e consulto especialistas em produto e marketing para recomendações.
                                </p>

                                {/* Quick actions */}
                                <div className="w-full">
                                    <div className="flex items-center gap-1 text-[10px] text-foreground-muted mb-2">
                                        <Lightbulb className="w-3 h-3" />
                                        Análises rápidas
                                    </div>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {QUICK_ANALYSES.map((action) => (
                                            <button
                                                key={action.id}
                                                onClick={() => handleQuickAction(action.id)}
                                                className="text-left px-3 py-2 bg-surface/80 border border-border rounded-lg text-xs text-foreground-muted hover:text-foreground hover:border-border-strong transition-all"
                                            >
                                                {action.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {messages.map((msg, i) => (
                                    <MessageBubble key={i} message={msg} />
                                ))}
                                {isLoading && (
                                    <div className="flex items-center gap-2 text-foreground-muted text-xs py-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Analisando dados com Claude...
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </>
                        )}
                        </div>
                    </ScrollArea>

                    {/* Quick actions bar (after first message) */}
                    {messages.length > 0 && (
                        <div className="px-3 py-1.5 border-t border-surface">
                            <ScrollArea className="w-full">
                                <div className="flex items-center gap-1 pb-1">
                                    {QUICK_ANALYSES.map((action) => (
                                        <button
                                            key={action.id}
                                            onClick={() => handleQuickAction(action.id)}
                                            disabled={isLoading}
                                            className="flex-shrink-0 px-2 py-1 bg-surface/60 border border-border rounded text-[10px] text-foreground-muted hover:text-foreground-secondary hover:border-border-strong transition-all disabled:opacity-50"
                                        >
                                            {action.label}
                                        </button>
                                    ))}
                                </div>
                                <ScrollBar orientation="horizontal" />
                            </ScrollArea>
                        </div>
                    )}

                    {/* Input */}
                    <form onSubmit={handleSubmit} className="p-3 border-t border-border">
                        <div className="flex items-center gap-2">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Pergunte ao CFO..."
                                disabled={isLoading}
                                className="flex-1 bg-surface border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-placeholder focus:outline-none focus:border-cyan-600 disabled:opacity-50"
                            />
                            <button
                                type="submit"
                                disabled={!input.trim() || isLoading}
                                className="p-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                    </form>
                </>
            )}
        </div>
    );
}
